/**
 * Appliance software licences: the price catalogue, the pricing rules, the
 * key format, and the portal contract for buying and storing keys.
 *
 * One module read by the public licence builder, the portal, the cloud (which
 * prices and signs) and the appliance (which verifies). The browser's price is
 * only ever a preview — the cloud recomputes every quote with `quoteLicense`
 * and never trusts a price the client sends.
 *
 * Decisions (2026-09-14, from the product owner):
 * - Perpetual keys. Optional yearly maintenance (updates + support) is a
 *   separate subscription and is never encoded in the key.
 * - Every key is locked to one appliance's hardware ID. Keys without one are
 *   rejected by the appliance.
 * - No transfers. A licence stays with its hardware ID for life.
 * - Certified partners get their account's discount applied automatically.
 * - Trials (staff-issued, time-limited keys) run side by side with a purchased
 *   licence and never disable anything already bought. They are not owned for
 *   pricing, not sales, and never supersede or get superseded by a purchase.
 *
 * All prices are PLACEHOLDERS pending a pricing decision.
 */

import type { FeatureFlag, LicenseTier } from '../types/features';
import { FEATURE_METADATA, TIER_FEATURES } from '../types/features';

// ── Keys ───────────────────────────────────────────────────────────────────

export const LICENSE_KEY_PREFIX = 'SCADA-';
export const LICENSE_KEY_VERSION = 2;
export const LICENSE_ISSUER = 'scada.ink';

/**
 * Ed25519 public key (SPKI DER, base64) that licence signatures verify
 * against. Built into the appliance rather than read from the environment:
 * an environment override is a one-line way to accept self-signed keys.
 * The private half is the cloud's LICENSE_SIGNING_KEY (and, for staff tooling,
 * scripts/.license-key.pem). Rotating it invalidates every issued key.
 */
export const LICENSE_PUBLIC_KEY_SPKI_DER_B64 = 'MCowBQYDK2VwAyEAOLOpckMG3I6K3OS1HzANogTSoQYEoUboxo0B45vYhEU=';

/** The signed body of a key: `SCADA-<base64url(JSON)>.<base64url(signature)>`. */
export interface LicenseKeyPayload {
  iss: typeof LICENSE_ISSUER;
  /** Key format version. Appliances reject anything but LICENSE_KEY_VERSION. */
  v: typeof LICENSE_KEY_VERSION;
  /** Licence id in the cloud, so a key can be matched to its record. */
  lid: string;
  /** Customer (company) name. */
  sub: string;
  /** Display tier derived from the features; the feature list is authoritative. */
  tier: LicenseTier;
  /** Every licensed feature, including the free base set. */
  features: FeatureFlag[];
  /** Normalised hardware ID of the one appliance this key works on. */
  hwid: string;
  /** Product edition; absent = scada. See LicenseEdition. */
  edition?: LicenseEdition;
  /** Expiry, Unix seconds. 0 = never. See `licenseKeyKind` for how it relates to `kind`. */
  exp: number;
  /** Issued at, Unix seconds. */
  iat: number;
  /**
   * What the key is. Absent = perpetual, so every key issued before trials
   * existed keeps working unchanged.
   *
   * - `perpetual`: a purchase (or complimentary licence). `exp` must be 0.
   *   An appliance holds ONE perpetual key; activating another replaces it.
   * - `trial`: time-limited. `exp` must be > 0. An appliance holds up to
   *   MAX_TRIAL_KEYS trial keys ALONGSIDE its perpetual key: a trial adds
   *   features until it expires and never replaces, disables or shortens
   *   anything the perpetual key grants.
   *
   * Legacy: staff `--days` keys issued before this field existed have no
   * `kind` and `exp > 0`; they are treated as trials. A key with
   * `kind: 'perpetual'` and `exp > 0`, or `kind: 'trial'` and `exp` 0, is
   * malformed and rejected.
   */
  kind?: LicenseKeyKind;
}

export type LicenseKeyKind = 'perpetual' | 'trial';

/** Trial keys an appliance stores at once, alongside its one perpetual key. */
export const MAX_TRIAL_KEYS = 5;

/**
 * The kind of a (signature-verified) payload, or null when `kind` and `exp`
 * contradict each other. Absent kind: `exp` 0 = perpetual, `exp` > 0 = a legacy
 * staff trial.
 */
export function licenseKeyKind(payload: Pick<LicenseKeyPayload, 'exp'> & { kind?: unknown }): LicenseKeyKind | null {
  const exp = typeof payload.exp === 'number' ? payload.exp : 0;
  if (payload.kind === undefined) return exp > 0 ? 'trial' : 'perpetual';
  if (payload.kind === 'perpetual') return exp === 0 ? 'perpetual' : null;
  if (payload.kind === 'trial') return exp > 0 ? 'trial' : null;
  return null;
}

/**
 * Hardware IDs are 16 hex characters (a Raspberry Pi CPU serial) or 32 (a
 * keyed hash of the OS machine ID). Customers copy them from the appliance's
 * Licence card, so separators, spaces and case are forgiven.
 */
export function normalizeHardwareId(raw: string): string | null {
  const hex = raw.toLowerCase().replace(/[^0-9a-f]/g, '');
  if (raw.replace(/[\s:-]/g, '').length !== hex.length) return null; // non-hex characters
  return hex.length === 16 || hex.length === 32 ? hex : null;
}

/** `10000000a1b2c3d4` → `1000-0000-a1b2-c3d4`, for display and copying. */
export function formatHardwareId(hwid: string): string {
  return hwid.match(/.{1,4}/g)?.join('-') ?? hwid;
}

function base64UrlToUtf8(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(input.length / 4) * 4, '=');
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Read a key's payload WITHOUT checking its signature — for display only.
 * Anything that grants a feature must verify the signature (node crypto) first.
 */
export function decodeLicenseKey(key: string): LicenseKeyPayload | null {
  const trimmed = key.trim();
  if (!trimmed.startsWith(LICENSE_KEY_PREFIX)) return null;
  const body = trimmed.slice(LICENSE_KEY_PREFIX.length);
  const dot = body.lastIndexOf('.');
  if (dot <= 0) return null;
  try {
    const payload = JSON.parse(base64UrlToUtf8(body.slice(0, dot))) as LicenseKeyPayload;
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

// ── Catalogue ──────────────────────────────────────────────────────────────

/**
 * Included free in every key: the Community tier, plus ACME certificates,
 * which no tier granted and which nobody should have to buy to get HTTPS,
 * plus mesh networking (paired appliances; included since 2026-10-17, was a
 * $300 feature). Included is not the same as on: a base feature still has an
 * admin toggle, and mesh starts switched off (migration 270) until an admin
 * turns it on in Settings → Feature Toggles. Keys issued while mesh was sold
 * still list it; that stays valid and changes nothing.
 */
// Event Streams (2026-10-19): included and off by default (migration 301)
// until the user decides its licensing.
// Redundancy (2026-10-19): included and off by default (migration 350) until
// the user decides its licensing.
// Python scripting (2026-10-19): included and off by default (migration 360),
// per the Python scripting plan (training/10-python-scripting-plan.md §8).
// Data connectors (2026-10-19): included and off by default (migration 310)
// until the user decides its licensing.
// Trend alarms and Change Trace (2026-10-21): included and off by default
// (migration 410) until the user decides their licensing.
export const LICENSE_BASE_FEATURES: FeatureFlag[] = [...TIER_FEATURES.community, 'acme_certificates', 'mesh_networking', 'event_streams', 'redundancy', 'python_scripting', 'data_connectors', 'trend_alarms', 'change_trace'];

/** One-time list price per feature, in whole US dollars. PLACEHOLDERS. */
export const LICENSE_FEATURE_PRICES_USD = {
  tag_udts: 400,
  dashboard_templates: 400,
  oee: 900,
  downtime_tracking: 600,
  quality_tracking: 500,
  andon: 400,
  labor_efficiency: 500,
  alarm_pipelines: 500,
  sms_voice_notifications: 200,
  reporting: 900,
  isa_alarm_metrics: 400,
  store_forward: 400,
  transaction_groups: 400,
  rest_api_outbound: 300,
  work_orders: 900,
  recipe_management: 700,
  work_instructions: 700,
  symbol_factory: 300,
  ladder_logic: 800,
  sfc_logic: 600,
  mqtt_sparkplug: 600,
  remote_databases: 500,
  // Its own component, in no tier or preset — set by the user 2026-10-16.
  machine_vision: 500,
} as const satisfies Partial<Record<FeatureFlag, number>>;

export type LicensableFeature = keyof typeof LICENSE_FEATURE_PRICES_USD;

export const LICENSABLE_FEATURES = Object.keys(LICENSE_FEATURE_PRICES_USD) as LicensableFeature[];

export function isLicensableFeature(flag: string): flag is LicensableFeature {
  return Object.prototype.hasOwnProperty.call(LICENSE_FEATURE_PRICES_USD, flag);
}

/**
 * A new Scada.Ink system: one appliance with the core platform (the base
 * features above). The software is useless without the hardware, so the
 * public price starts here. Shown on the pricing page and the public builder
 * for NEW systems only — quoteLicense never adds it, because online checkout
 * is always for an appliance the customer already has. Set by the user
 * 2026-10-15 "for now".
 */
export const APPLIANCE_BASE_PRICE_USD = 1499;

/** The "All" licence: every licensable feature, at a bundle price. PLACEHOLDER. */
export const ALL_LICENSE_PRICE_USD = 6995;

/** Yearly maintenance as a fraction of the licence's value. PLACEHOLDER. */
export const MAINTENANCE_RATE = 0.2;

/**
 * Features that do nothing without another. Selecting one selects its
 * requirements, so the builder can never sell a feature that cannot run.
 *   sms_voice_notifications — sent from an alarm pipeline step
 *   sfc_logic — SFC routines run on the ladder scan engine
 *   dashboard_templates — templates bind to UDT instances
 *   labor_efficiency — earned hours are computed per work order
 */
export const LICENSE_DEPENDENCIES: Partial<Record<LicensableFeature, LicensableFeature[]>> = {
  sms_voice_notifications: ['alarm_pipelines'],
  sfc_logic: ['ladder_logic'],
  dashboard_templates: ['tag_udts'],
  labor_efficiency: ['work_orders'],
};

/** Groups for the builder, in display order, using the product's own categories. */
export const LICENSE_GROUPS: Array<{ id: string; name: string; features: LicensableFeature[] }> = (() => {
  const order = ['production', 'alarms_reporting', 'mes', 'control', 'vision', 'data', 'connectivity', 'tag_system', 'visualization'] as const;
  const names: Record<(typeof order)[number], string> = {
    production: 'Production analytics',
    alarms_reporting: 'Alarms & reporting',
    mes: 'Manufacturing execution',
    control: 'Control & logic',
    vision: 'Machine vision',
    data: 'Data infrastructure',
    connectivity: 'Connectivity',
    tag_system: 'Tag modeling',
    visualization: 'Visualization',
  };
  return order
    .map((cat) => ({ id: cat, name: names[cat], features: LICENSABLE_FEATURES.filter((f) => FEATURE_METADATA[f].category === cat) }))
    .filter((g) => g.features.length > 0);
})();

/** One-click selections in the builder. */
export const LICENSE_PRESETS: Array<{ id: 'professional' | 'enterprise'; name: string; features: LicensableFeature[] }> = [
  { id: 'professional', name: 'Professional features', features: TIER_FEATURES.professional.filter(isLicensableFeature) },
  {
    id: 'enterprise',
    name: 'Professional + MES',
    features: [...TIER_FEATURES.professional, ...TIER_FEATURES.enterprise].filter(isLicensableFeature),
  },
];

/** Add every requirement of every selected feature. */
export function withDependencies(features: Iterable<string>): LicensableFeature[] {
  const out = new Set<LicensableFeature>();
  const visit = (f: LicensableFeature) => {
    if (out.has(f)) return;
    out.add(f);
    for (const dep of LICENSE_DEPENDENCIES[f] ?? []) visit(dep);
  };
  for (const f of features) if (isLicensableFeature(f)) visit(f);
  return LICENSABLE_FEATURES.filter((f) => out.has(f));
}

/** The tier label shown on the appliance for a set of features. */
export function tierForFeatures(features: Iterable<string>): LicenseTier {
  const set = new Set(features);
  const has = (list: FeatureFlag[]) => list.every((f) => set.has(f));
  if (has(TIER_FEATURES.professional) && has(TIER_FEATURES.enterprise)) return 'enterprise';
  if (has(TIER_FEATURES.professional)) return 'professional';
  return 'community';
}

const sumList = (features: Iterable<LicensableFeature>) => [...features].reduce((s, f) => s + LICENSE_FEATURE_PRICES_USD[f], 0);

// ── Pricing ────────────────────────────────────────────────────────────────

// ── Editions ───────────────────────────────────────────────────────────────

/**
 * scada: the full Scada.Ink platform, priced per feature or All.
 * hmi: HMI.ink — a slimmer display product (connect, browse tags, dashboards,
 * custom pages, buttons; no historian, API, AI or the rest). A flat price per
 * appliance with optional flat yearly support.
 *
 * The edition is recorded in the signed key. Today's Scada.Ink software
 * ignores it (an HMI.ink key grants only the free base set there); the future
 * HMI.ink build reads it. Absent = scada, so every key already issued is a
 * Scada.Ink key.
 */
export type LicenseEdition = 'scada' | 'hmi';

/** PLACEHOLDERS pending a pricing decision. */
export const HMI_LICENSE_PRICE_USD = 999;
export const HMI_SUPPORT_YEARLY_USD = 199;
/**
 * Upgrading an HMI.ink appliance to Scada.Ink credits what was paid for
 * HMI.ink against the first Scada.Ink purchase, never below zero.
 */
export const HMI_UPGRADE_CREDIT_USD = 999;

export const EDITION_NAMES: Record<LicenseEdition, string> = { scada: 'Scada.Ink', hmi: 'HMI.ink' };

export interface LicenseQuoteInput {
  /** Requested features. Dependencies are added. Ignored when `all` is set. */
  features: Iterable<string>;
  all: boolean;
  /** Features already licensed on this hardware ID by an earlier purchase. */
  owned?: Iterable<string>;
  /** Partner discount, 0–50. */
  discountPct?: number;
  maintenance?: boolean;
  /** What is being bought. Default scada. */
  edition?: LicenseEdition;
  /** Edition of the active perpetual licence already on this hardware ID, if any. */
  ownedEdition?: LicenseEdition | null;
}

export interface LicenseQuoteLine {
  kind: 'feature' | 'all' | 'hmi' | 'credit' | 'discount';
  label: string;
  feature?: LicensableFeature;
  amountUsd: number;
}

export interface LicenseQuote {
  /** Everything the issued key will contain: base + owned + new. */
  features: FeatureFlag[];
  /** Licensable features being bought now. */
  newFeatures: LicensableFeature[];
  /** Licensable features that were already owned. */
  ownedFeatures: LicensableFeature[];
  isAll: boolean;
  tier: LicenseTier;
  lines: LicenseQuoteLine[];
  /** List price of what is being bought, before credit and discount. */
  listUsd: number;
  /** Reduction for features already owned when upgrading to All. */
  upgradeCreditUsd: number;
  discountPct: number;
  discountUsd: number;
  /** Due now. */
  totalUsd: number;
  /** Yearly maintenance for the whole resulting licence, after discount. 0 if not requested. */
  maintenanceYearlyUsd: number;
  /** Nothing new is being bought. */
  empty: boolean;
  edition: LicenseEdition;
  /** Why this can't be bought for this appliance (e.g. HMI.ink over Scada.Ink), or null. */
  blockedReason: string | null;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * The price of a licence, or of an upgrade to one.
 *
 * À la carte: the list price of each feature not already owned.
 * All: the bundle price, less the list value of features already owned — but
 * never more than buying the missing features individually, so a customer one
 * feature short of everything is never charged the whole bundle.
 * Partner discount comes off last. Maintenance is a yearly percentage of the
 * whole licence's value (bundle price for All), with the same discount.
 */
export function quoteLicense(input: LicenseQuoteInput): LicenseQuote {
  const discountPct = Math.max(0, Math.min(50, Math.round(input.discountPct ?? 0)));
  if (input.edition === 'hmi') return quoteHmi(input, discountPct);
  const owned = withDependencies(input.owned ?? []);
  const ownedSet = new Set<LicensableFeature>(owned);
  const wantedAll = input.all;
  const wanted = wantedAll ? LICENSABLE_FEATURES : withDependencies([...(input.features as Iterable<string>)]);

  const resulting = new Set<LicensableFeature>([...owned, ...wanted]);
  const newFeatures = LICENSABLE_FEATURES.filter((f) => resulting.has(f) && !ownedSet.has(f));
  const isAll = resulting.size === LICENSABLE_FEATURES.length;

  const lines: LicenseQuoteLine[] = [];
  let listUsd = sumList(newFeatures);
  let upgradeCreditUsd = 0;
  // An HMI.ink appliance moving to Scada.Ink owns no Scada.Ink features, but
  // its HMI.ink purchase is credited (applied after the lines are chosen).
  const hmiCredit = input.ownedEdition === 'hmi' && newFeatures.length > 0;

  if (isAll && newFeatures.length > 0) {
    const bundleCost = Math.max(0, ALL_LICENSE_PRICE_USD - sumList(owned));
    if (bundleCost < listUsd) {
      lines.push({ kind: 'all', label: 'All features license', amountUsd: ALL_LICENSE_PRICE_USD });
      if (owned.length) {
        upgradeCreditUsd = ALL_LICENSE_PRICE_USD - bundleCost;
        lines.push({ kind: 'credit', label: 'Credit for features you already own', amountUsd: -upgradeCreditUsd });
      }
      listUsd = ALL_LICENSE_PRICE_USD;
    } else {
      for (const f of newFeatures) lines.push({ kind: 'feature', feature: f, label: FEATURE_METADATA[f].name, amountUsd: LICENSE_FEATURE_PRICES_USD[f] });
    }
  } else {
    for (const f of newFeatures) lines.push({ kind: 'feature', feature: f, label: FEATURE_METADATA[f].name, amountUsd: LICENSE_FEATURE_PRICES_USD[f] });
  }

  if (hmiCredit) {
    const credit = Math.min(HMI_UPGRADE_CREDIT_USD, listUsd - upgradeCreditUsd);
    if (credit > 0) {
      upgradeCreditUsd += credit;
      lines.push({ kind: 'credit', label: 'Credit for your HMI.ink license', amountUsd: -credit });
    }
  }

  const beforeDiscount = listUsd - upgradeCreditUsd;
  const discountUsd = round2((beforeDiscount * discountPct) / 100);
  if (discountUsd > 0) lines.push({ kind: 'discount', label: `Partner discount (${discountPct}%)`, amountUsd: -discountUsd });
  const totalUsd = round2(beforeDiscount - discountUsd);

  const licenceValue = isAll ? ALL_LICENSE_PRICE_USD : sumList(resulting);
  const maintenanceYearlyUsd = input.maintenance ? round2(licenceValue * MAINTENANCE_RATE * (1 - discountPct / 100)) : 0;

  const features: FeatureFlag[] = [...new Set<FeatureFlag>([...LICENSE_BASE_FEATURES, ...LICENSABLE_FEATURES.filter((f) => resulting.has(f))])];

  return {
    features,
    newFeatures,
    ownedFeatures: owned,
    isAll,
    tier: tierForFeatures(features),
    lines,
    listUsd,
    upgradeCreditUsd,
    discountPct,
    discountUsd,
    totalUsd,
    maintenanceYearlyUsd,
    empty: newFeatures.length === 0,
    edition: 'scada',
    blockedReason: null,
  };
}

function quoteHmi(input: LicenseQuoteInput, discountPct: number): LicenseQuote {
  const base: LicenseQuote = {
    features: [...LICENSE_BASE_FEATURES],
    newFeatures: [],
    ownedFeatures: withDependencies(input.owned ?? []),
    isAll: false,
    tier: 'community',
    lines: [],
    listUsd: 0,
    upgradeCreditUsd: 0,
    discountPct,
    discountUsd: 0,
    totalUsd: 0,
    maintenanceYearlyUsd: 0,
    empty: true,
    edition: 'hmi',
    blockedReason: null,
  };
  if (input.ownedEdition === 'scada' || [...(input.owned ?? [])].length > 0) {
    return { ...base, blockedReason: 'This appliance already has a Scada.Ink license, which includes everything HMI.ink does.' };
  }
  if (input.ownedEdition === 'hmi') {
    return { ...base, blockedReason: 'This appliance already has an HMI.ink license.' };
  }
  const discountUsd = round2((HMI_LICENSE_PRICE_USD * discountPct) / 100);
  const lines: LicenseQuoteLine[] = [{ kind: 'hmi', label: 'HMI.ink license', amountUsd: HMI_LICENSE_PRICE_USD }];
  if (discountUsd > 0) lines.push({ kind: 'discount', label: `Partner discount (${discountPct}%)`, amountUsd: -discountUsd });
  return {
    ...base,
    lines,
    listUsd: HMI_LICENSE_PRICE_USD,
    discountUsd,
    totalUsd: round2(HMI_LICENSE_PRICE_USD - discountUsd),
    maintenanceYearlyUsd: input.maintenance ? round2(HMI_SUPPORT_YEARLY_USD * (1 - discountPct / 100)) : 0,
    empty: false,
  };
}

// ── Portal contract ────────────────────────────────────────────────────────
//
//   POST   /api/portal/licenses/quote      LicenseQuoteRequest → LicenseQuoteResponse
//   POST   /api/portal/licenses/checkout   LicenseQuoteRequest → LicenseCheckoutResponse
//   GET    /api/portal/licenses            → PortalLicense[]  (trials included: active, and expired within 30 days)
//   POST   /api/portal/licenses/import     ImportLicenseRequest → PortalLicense
//   PATCH  /api/portal/licenses/:id        UpdateLicenseRequest → PortalLicense
//   DELETE /api/portal/licenses/:id        imported keys only
//   POST   /api/portal/licenses/:id/maintenance   start maintenance → LicenseCheckoutResponse
//   POST   /api/portal/licenses/:id/maintenance/cancel → PortalLicense

export interface LicenseQuoteRequest {
  hardwareId: string;
  features: FeatureFlag[];
  all: boolean;
  maintenance: boolean;
  /** Optional name for the licence, e.g. the appliance it is for. */
  label?: string;
  /** What is being bought; default scada. HMI.ink ignores features/all. */
  edition?: LicenseEdition;
}

export interface LicenseQuoteResponse {
  quote: LicenseQuote;
  hardwareId: string;
  /** The account's active licence on this hardware ID, which a purchase upgrades. */
  existingLicenseId: string | null;
  /** A registered appliance whose heartbeat reports this hardware ID. */
  applianceName: string | null;
  /** Maintenance is already running on the existing licence. */
  maintenanceActive: boolean;
  /**
   * Unexpired trials this account holds on this hardware ID. Never counted as
   * owned: the quote still charges for a feature being trialled. `features`
   * lists licensable features only (not the free base set).
   */
  activeTrials: Array<{ licenseId: string; features: FeatureFlag[]; expiresAt: string }>;
}

/**
 * `expired` is derived, never stored: a trial whose `expiresAt` has passed.
 * Purchases and complimentary licences never expire.
 */
export type LicenseStatus = 'pending_payment' | 'active' | 'superseded' | 'void' | 'expired';

export interface PortalLicenseMaintenance {
  active: boolean;
  yearlyUsd: number;
  renewsAt: string | null;
  cancelAtPeriodEnd: boolean;
  provider: 'stripe' | 'invoice';
}

export interface PortalLicense {
  id: string;
  /** Product edition of this licence. */
  edition: LicenseEdition;
  /** purchase: bought in the portal (or issued by staff). import: an existing key stored as a backup. */
  source: 'purchase' | 'import';
  /**
   * perpetual: bought or complimentary, never expires. trial: time-limited,
   * issued by Scada.Ink staff, runs alongside the perpetual licence on the same
   * appliance and never replaces it.
   */
  kind: LicenseKeyKind;
  /** Trials: when the key stops working (ISO). Null for perpetual licences. */
  expiresAt: string | null;
  status: LicenseStatus;
  label: string;
  hardwareId: string;
  applianceName: string | null;
  features: FeatureFlag[];
  tier: LicenseTier;
  isAll: boolean;
  /** The key itself. Null until payment completes. */
  key: string | null;
  /** Imports: whether the key's signature verified against LICENSE_PUBLIC_KEY_SPKI_DER_B64. */
  signatureValid: boolean;
  pricePaidUsd: number | null;
  discountPct: number;
  maintenance: PortalLicenseMaintenance | null;
  /** The licence this one replaced, when it was an upgrade. */
  supersedesId: string | null;
  issuedAt: string | null;
  createdAt: string;
  /**
   * Import responses only: whether storing the key also registered its
   * appliance to this account, and why not when it didn't (e.g. another
   * account bought the licence for that hardware ID).
   */
  registration?: { registered: boolean; message: string };
}

export type LicenseCheckoutResponse =
  | { kind: 'redirect'; url: string }
  | { kind: 'issued'; license: PortalLicense };

export interface ImportLicenseRequest {
  key: string;
  label?: string;
}

export interface UpdateLicenseRequest {
  label: string;
}
